import React, { useEffect, useRef } from 'react';
import { useInternationalAddress } from '../components/hooks/useInternationalAddress';
import CountrySelector from './CountrySelector';
import AddressField from './AddressField';

const InternationalAddressForm = ({ 
    section, 
    externalFormData,
    onDataChange,
    onValidationChange,
    readOnly = false 
}) => {
    const {
        selectedCountry,
        countryConfig,
        formData,
        errors,
        touched,
        provinceOptions,
        supportedCountries,
        updateField,
        validateAll,
        changeCountry,
        initializeForm
    } = useInternationalAddress(externalFormData?.nazione || 'IT');

    const isInitialized = useRef(false);
    const prevExternalData = useRef(externalFormData);
    const isUpdatingFromParent = useRef(false);  // ✅ Previene loop
    const timeoutRef = useRef(null);    // ✅ Debounce per validazione
    
    // Inizializzazione
    useEffect(() => {
        const hasDataChanged = JSON.stringify(prevExternalData.current) !== JSON.stringify(externalFormData);
        
        if (hasDataChanged && !isUpdatingFromParent.current) {
            prevExternalData.current = externalFormData;
            
            if (externalFormData && Object.keys(externalFormData).length > 1) {
                initializeForm(externalFormData);
            } else if (!isInitialized.current) {
                initializeForm();
                isInitialized.current = true;
            }
        }
    }, [externalFormData, initializeForm]);
    
    // ✅ Notifica il parent SOLO quando formData cambia davvero (con debounce)
    useEffect(() => {
        if (onDataChange && Object.keys(formData).length > 0) {
            // Pulisci timeout precedente
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            
            // Debounce per evitare loop
            timeoutRef.current = setTimeout(() => {
                isUpdatingFromParent.current = true;
                onDataChange(section, {
                    ...formData,
                    nazione: selectedCountry
                });
                // Resetta il flag dopo l'aggiornamento
                setTimeout(() => {
                    isUpdatingFromParent.current = false;
                }, 50);
            }, 100);
        }
        
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [formData, selectedCountry, section, onDataChange]);
    
    // ✅ Validazione con debounce
    useEffect(() => {
        if (onValidationChange && Object.keys(formData).length > 0) {
            const timer = setTimeout(() => {
                const allValid = validateAll();
                onValidationChange(section, allValid);
            }, 150);
            
            return () => clearTimeout(timer);
        }
    }, [formData, section, onValidationChange, validateAll]);
    
    // Gestione cambio paese
    const handleCountryChange = (newCountry) => {
        changeCountry(newCountry);
        if (onDataChange) {
            isUpdatingFromParent.current = true;
            onDataChange(section, { nazione: newCountry });
            setTimeout(() => {
                isUpdatingFromParent.current = false;
            }, 50);
        }
    };
    
    // Filtra i campi fiscali
    const isFiscalField = (fieldName) => {
        const fiscalFields = ['codFiscale', 'partitaIva', 'nif', 'codiceFiscale', 'emailFatturazione'];
        return fiscalFields.includes(fieldName);
    };
    
    const addressFields = countryConfig?.fields?.filter(field => !isFiscalField(field.name)) || [];
    const fieldOrder = countryConfig?.fieldOrder || addressFields.map(f => f.name);
    
    return (
        <div className="international-address-form">
            <CountrySelector
                selectedCountry={selectedCountry}
                countries={supportedCountries}
                onChange={handleCountryChange}
                disabled={readOnly}
            />
            
            <div className="address-fields">
                {fieldOrder.map(fieldName => {
                    const field = addressFields.find(f => f.name === fieldName);
                    if (!field) return null;
                    
                    const options = field.type === 'select' ? provinceOptions : null;
                    const value = formData[field.name] || '';
                    const originalError = errors[field.name];
                    const isTouched = touched[field.name];
                    
                    // ✅ Messaggio personalizzato per la provincia
                    let errorMessage = originalError;
                    const isProvinceField = fieldName === 'provincia' || fieldName === 'province' || fieldName === 'stato' || fieldName === 'state';
                    
                    if (isProvinceField && isTouched && (!value || value === '')) {
                        errorMessage = '⚠️ Seleziona una provincia dall\'elenco.';
                    }
                    
                    return (
                        <AddressField
                            key={field.name}
                            field={field}
                            value={value}
                            error={errorMessage}
                            touched={isTouched}
                            onChange={updateField}
                            onBlur={updateField}
                            options={options}
                            disabled={readOnly}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default InternationalAddressForm;
